import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { AuthFacade } from '@ideapa/app-auth';
import { NavigationFacade } from '@ideapa/app-navigation';
import { IconButtonComponent } from '@ideapa/app-ui-kit';
import { map, startWith } from 'rxjs';
import { MENU } from './menu';
import { MenuItem } from './menu.model';

@Component({
  standalone: false,
  selector: 'ideapa-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, AfterViewInit {
  @ViewChild('sidebarToggler') sidebarToggler?: IconButtonComponent;

  menuItems: MenuItem[] = [];

  @ViewChild('sidebarMenu') sidebarMenu?: ElementRef;

  modulo$ = this.authFacade.modulo$;

  tabs$ = this.navigationFacade.tabs$;

  formControl = new FormControl();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    router: Router,
    private authFacade: AuthFacade,
    private navigationFacade: NavigationFacade
  ) {
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        /**
         * Activating the current active item dropdown
         */
        // this._activateMenuDropdown();

        /**
         * closing the sidebar
         */
        if (window.matchMedia('(max-width: 991px)').matches) {
          this.document.body.classList.remove('sidebar-open');
        }
      }
    });
  }

  checkHierarchyPermissions(item: MenuItem) {
    const permissions: string[] = [...(item.permissions || [])];

    item.subItems?.forEach((si) => {
      if (si.permissions) {
        permissions.push(...this.checkHierarchyPermissions(si));
      }
    });
    return permissions;
  }

  isTabOpened(path: string) {
    return this.tabs$.pipe(
      map((t) => t.find((tab) => tab.path === path)?.tabOpened)
    );
  }

  private filterMenuByLabel(
    currentMenu: MenuItem[],
    searchTerm: string
  ): MenuItem[] {
    if (!currentMenu || currentMenu.length === 0) {
      // console.log(currentMenu, typeof currentMenu);
      return [];
    }

    const filteredMenu = [currentMenu[0], currentMenu[1]];
    const remainedMenu = currentMenu.slice(2);

    for (const menuItem of remainedMenu) {
      const newMenuItem = this.buildMenuItemTreeByLabel(menuItem, searchTerm);
      if (newMenuItem) filteredMenu.push(newMenuItem);
    }

    return filteredMenu;
  }

  private buildMenuItemTreeByLabel(
    menuItem: MenuItem,
    searchTerm: string
  ): MenuItem | null {
    return menuItem.subItems
      ? this.traverseAndBuildMenuItemTreeByLabel(menuItem, searchTerm)
      : this.traverseAndBuildMenuItemByLabel(menuItem, searchTerm);
  }

  private traverseAndBuildMenuItemTreeByLabel(
    menuItem: MenuItem,
    value: string
  ): MenuItem | null {
    const subItems = menuItem.subItems ?? [];

    const validMenuItems: MenuItem[] = [];

    for (const subItem of subItems) {
      const childMenuItem = this.buildMenuItemTreeByLabel(subItem, value);
      childMenuItem && validMenuItems.push(childMenuItem);
    }

    return validMenuItems.length
      ? {
          ...menuItem,
          subItems: validMenuItems,
        }
      : null;
  }

  private traverseAndBuildMenuItemByLabel(
    menuItem: MenuItem,
    value: string
  ): MenuItem | null {
    return this.isLabelMatchingSearchTerm(menuItem.label, value)
      ? { ...menuItem }
      : null;
  }

  private isLabelMatchingSearchTerm(
    label: string | undefined,
    searchTerm: string
  ): boolean {
    if (!label) return false;

    const labelLowercase = label.toLowerCase().trim();
    const searchTermLowercase = searchTerm.toLowerCase().trim();
    const labelContainsSearchTerm =
      labelLowercase.includes(searchTermLowercase);

    return labelContainsSearchTerm;
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        startWith(''),
        map((enteredLabel) => {
          return enteredLabel
            ? this.filterMenuByLabel(MENU, enteredLabel)
            : MENU;
        })
      )
      .subscribe((filteredMenuItems) => {
        this.menuItems = filteredMenuItems;
      });

    /**
     * Sidebar-folded on desktop (min-width:992px and max-width: 1199px)
     */
    const desktopMedium = window.matchMedia(
      '(min-width:992px) and (max-width: 1199px)'
    );
    desktopMedium.addEventListener('change', () => {
      this.iconSidebar;
    });
    this.iconSidebar(desktopMedium);
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngAfterViewInit() {
    // activate menu item
    // if (this.sidebarMenu) new MetisMenu(this.sidebarMenu.nativeElement);
    // this._activateMenuDropdown();
  }

  /**
   * Toggle sidebar on hamburger button click
   */
  toggleSidebar(e: Event) {
    // this.sidebarToggler?..classList.toggle('active');
    // this.sidebarToggler?.nativeElement.classList.toggle('not-active');
    if (window.matchMedia('(min-width: 992px)').matches) {
      e.preventDefault();
      this.document.body.classList.toggle('sidebar-folded');
    } else if (window.matchMedia('(max-width: 991px)').matches) {
      e.preventDefault();
      this.document.body.classList.toggle('sidebar-open');
    }
  }

  /**
   * Toggle settings-sidebar
   */
  toggleSettingsSidebar(e: Event) {
    e.preventDefault();
    this.document.body.classList.toggle('settings-open');
  }

  /**
   * Open sidebar when hover (in folded folded state)
   */
  operSidebarFolded() {
    if (this.document.body.classList.contains('sidebar-folded')) {
      this.document.body.classList.add('open-sidebar-folded');
    }
  }

  /**
   * Fold sidebar after mouse leave (in folded state)
   */
  closeSidebarFolded() {
    if (this.document.body.classList.contains('sidebar-folded')) {
      this.document.body.classList.remove('open-sidebar-folded');
    }
  }

  /**
   * Sidebar-folded on desktop (min-width:992px and max-width: 1199px)
   */
  iconSidebar(mq: MediaQueryList) {
    if (mq.matches) {
      this.document.body.classList.add('sidebar-folded');
    } else {
      this.document.body.classList.remove('sidebar-folded');
    }
  }

  /**
   * Switching sidebar light/dark
   */
  onSidebarThemeChange(event: Event) {
    this.document.body.classList.remove('sidebar-light', 'sidebar-dark');
    this.document.body.classList.add((<HTMLInputElement>event.target).value);
    this.document.body.classList.remove('settings-open');
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  /**
   * Reset the menus then hilight current active menu item
   */
  // _activateMenuDropdown() {
  //   this.resetMenuItems();
  //   this.activateMenuItems();
  // }

  /**
   * Resets the menus
   */
  resetMenuItems() {
    const links = document.getElementsByClassName('nav-link-ref');

    for (let i = 0; i < links.length; i++) {
      const menuItemEl = links[i];
      menuItemEl.classList.remove('mm-active');
      const parentEl = menuItemEl.parentElement;

      if (parentEl) {
        parentEl.classList.remove('mm-active');
        const parent2El = parentEl.parentElement;

        if (parent2El) {
          parent2El.classList.remove('mm-show');
        }

        const parent3El = parent2El?.parentElement;
        if (parent3El) {
          parent3El.classList.remove('mm-active');

          if (parent3El.classList.contains('side-nav-item')) {
            const firstAnchor = parent3El.querySelector('.side-nav-link-a-ref');

            if (firstAnchor) {
              firstAnchor.classList.remove('mm-active');
            }
          }

          const parent4El = parent3El.parentElement;
          if (parent4El) {
            parent4El.classList.remove('mm-show');

            const parent5El = parent4El.parentElement;
            if (parent5El) {
              parent5El.classList.remove('mm-active');
            }
          }
        }
      }
    }
  }

  /**
   * Toggles the menu items
   */
  activateMenuItems() {
    const links: any = document.getElementsByClassName('nav-link-ref');

    let menuItemEl = null;

    for (let i = 0; i < links.length; i++) {
      // tslint:disable-next-line: no-string-literal
      if (window.location.pathname.indexOf(links[i]['pathname']) >= 0) {
        menuItemEl = links[i];

        break;
      }
    }

    if (menuItemEl) {
      menuItemEl.classList.add('mm-active');
      const parentEl = menuItemEl.parentElement;

      if (parentEl) {
        parentEl.classList.add('mm-active');

        const parent2El = parentEl.parentElement;
        if (parent2El) {
          parent2El.classList.add('mm-show');
        }

        const parent3El = parent2El.parentElement;
        if (parent3El) {
          parent3El.classList.add('mm-active');

          if (parent3El.classList.contains('side-nav-item')) {
            const firstAnchor = parent3El.querySelector('.side-nav-link-a-ref');

            if (firstAnchor) {
              firstAnchor.classList.add('mm-active');
            }
          }

          const parent4El = parent3El.parentElement;
          if (parent4El) {
            parent4El.classList.add('mm-show');

            const parent5El = parent4El.parentElement;
            if (parent5El) {
              parent5El.classList.add('mm-active');
            }
          }
        }
      }
    }
  }
}
